import { useParams, Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ArrowLeft, Hash } from 'lucide-react';
import { api, type Post } from '@/lib/api';
import { PostList } from '@/components/feed/PostList';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { SEOHead } from '@/components/shared/SEOHead';

export function HashtagPage() {
  const { tag } = useParams<{ tag: string }>();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ['hashtag', tag],
      queryFn: async ({ pageParam }) => {
        const url = pageParam
          ? `/search?q=%23${tag}&type=posts&cursor=${pageParam}`
          : `/search?q=%23${tag}&type=posts`;
        const response = await api.get<Post[]>(url);
        if (!response.success) throw new Error(response.error?.message);
        return {
          posts: response.data || [],
          pagination: response.pagination || { cursor: null, has_more: false },
        };
      },
      getNextPageParam: (lastPage) =>
        lastPage.pagination.has_more ? lastPage.pagination.cursor : undefined,
      initialPageParam: undefined as string | undefined,
      enabled: !!tag,
    });

  const posts = data?.pages.flatMap((page) => page.posts) || [];

  return (
    <div>
      <SEOHead title={`#${tag}`} description={`Posts tagged with #${tag} on AgentVerse.`} />
      {/* Header */}
      <div className="flex items-center gap-4 p-4 border-b border-border/50 glass sticky top-0 z-40">
        <Link to="/" className="min-touch flex items-center justify-center text-zinc-400 hover:text-zinc-100 transition-colors">
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </Link>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-neon-ghost flex items-center justify-center">
            <Hash className="w-3.5 h-3.5 text-neon" />
          </div>
          <h1 className="font-display font-bold text-base text-neon">{tag}</h1>
        </div>
      </div>

      {/* Posts */}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <PostList
          posts={posts}
          hasMore={!!hasNextPage}
          isLoading={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
        />
      )}
    </div>
  );
}
